import { createAsyncThunk } from '@reduxjs/toolkit';

import type { BuyItNowRequest } from '../../cart/models/cart.model';
import { AddToCartRequest } from '../../cart/models/cart.model';
import type { ApiError } from '../../models/api-error.enum';
import { parseApiError } from '../../models/api-error.enum';
import type { NeedAccessReq, NeedAccessResponse } from '../../need-access/need-access.interface';
import type { LineItemList } from '../../shopify/models/line-item-list.model';
import { shopifyService } from '../../shopify/shopify.service';
import { ContentService } from '../content.service';
import type {
  AccessResponse,
  VideoAccessResponse,
  VideoListAccessResponse,
} from '../models/access-response';
import type { CollectiblesResponse } from '../models/collectibles.interface';
import type {
  PromotedProductsReq,
  PromotedProductsResponse,
} from '../models/promoted-products.interface';
import type { VideoBarRequest } from '../models/video-bar.interface';

const contentService = new ContentService();
const cartService = shopifyService.cartService;

export const loadVideo = createAsyncThunk<VideoAccessResponse, string, { rejectValue: ApiError }>(
  'content/loadVideo',
  async (id, { rejectWithValue }) => {
    try {
      return await contentService.loadVideo(id);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const loadVideoBar = createAsyncThunk<
  VideoListAccessResponse,
  VideoBarRequest,
  { rejectValue: ApiError }
>('content/loadVideoBar', async (request, { rejectWithValue }) => {
  try {
    const videos = await contentService.loadVideoBar(request);
    if (request.instanceId && videos) {
      videos.id = request.instanceId;
    }
    return videos;
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const loadProductAccess = createAsyncThunk<
  AccessResponse,
  string,
  { rejectValue: ApiError }
>('content/loadProductAccess', async (id: string, { rejectWithValue }) => {
  try {
    return await contentService.loadProductAccess(id);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const loadNeedAccess = createAsyncThunk<
  NeedAccessResponse,
  NeedAccessReq,
  { rejectValue: ApiError }
>('content/loadNeedAccess', async (request: NeedAccessReq, { rejectWithValue }) => {
  try {
    return await contentService.loadNeedAccess(request);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const loadPromotedProducts = createAsyncThunk<
  PromotedProductsResponse,
  PromotedProductsReq,
  { rejectValue: ApiError }
>('content/loadPromotedProducts', async (request: PromotedProductsReq, { rejectWithValue }) => {
  try {
    return await contentService.loadPromotedProducts(request);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const buyItNow = createAsyncThunk<LineItemList, BuyItNowRequest, { rejectValue: ApiError }>(
  'content/buyItNow',
  async (request: BuyItNowRequest, { rejectWithValue }) => {
    try {
      return await cartService.addToCart(
        {
          productVariantId: request.productVariantId.toString(),
          sellingPlanId: request.sellingPlanId,
          membership: !!request.sellingPlanId,
          postId: request.postId,
        } as BuyItNowRequest,
        undefined,
        request.product
      );
    } catch (error) {
      console.log(error);
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const loadCollectibles = createAsyncThunk<
  CollectiblesResponse,
  void,
  { rejectValue: ApiError }
>('content/loadCollectibles', async (_, { rejectWithValue }) => {
  try {
    return await contentService.loadCollectibles();
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});
